import { useCompanyApi } from '@/api/client/api-client';
import type { MeDetailResponse } from '@/api/interfaces/me-detail.types';
import type { AvailableWireDoc } from '@/components/Views/Company/File/file.types';
import type { PaginatedResponse } from '@/interfaces/pagination.types';
import type { Branch, Company, Role } from '@/types/closinglock.types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// Show all the user's active companies, branches, and wire docs with fixed pagination
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 100;

const companyFetch = useCompanyApi({ auth: 'jwt' });

export const useUserStore = defineStore('user', () => {
    // file details
    const showBalanceDue = ref(false);
    const showCloseDate = ref(false);
    const showNotes = ref(false);

    // default wire doc
    const defaultWireDoc = ref<AvailableWireDoc>();

    // disbursements
    const isDisbursementApprover = ref(false);
    const isDisbursementApprovalsEnabled = ref(false);

    // client type requirements
    const idvDependentClientTypes = ref<Role[]>([]);
    const requireIdvClientTypes = ref<Role[]>([]);
    const requireMfaClientTypes = ref<Role[]>([]);

    // get me detail
    const {
        data: meDetail,
        isFetching: isFetchingMeDetail,
        onFetchResponse: onFetchMeDetailResponse,
    } = companyFetch('/api/user/me').json<MeDetailResponse>();

    onFetchMeDetailResponse(() => {
        defaultWireDoc.value = meDetail.value?.data?.default_wire_doc;
        idvDependentClientTypes.value = meDetail.value?.data?.account?.idv_dependent_client_types ?? [];
        isDisbursementApprover.value = meDetail.value?.data?.settings?.payments?.is_disbursement_approver ?? false;
        isDisbursementApprovalsEnabled.value = meDetail.value?.data?.account?.disbursement_approvals_enabled ?? false;
        requireIdvClientTypes.value = meDetail.value?.data?.account?.require_idv_client_types ?? [];
        requireMfaClientTypes.value = meDetail.value?.data?.account?.require_mfa_client_types ?? [];
        showBalanceDue.value = meDetail.value?.data?.account?.show_balance_due ?? false;
        showCloseDate.value = meDetail.value?.data?.account?.show_close_date ?? false;
        showNotes.value = meDetail.value?.data?.account?.show_notes ?? false;
    });

    // companies
    const {
        data: companies,
        isFetching: isFetchingCompanies,
        execute: executeCompanies,
    } = companyFetch(`/api/user/titles?active=true&page=${DEFAULT_PAGE}&per_page=${DEFAULT_PER_PAGE}`, { immediate: false }).json<
        PaginatedResponse<Company>
    >();

    const activeCompanies = computed<Company[]>(() => companies.value?.data ?? []);

    const defaultWireDocCompany = computed(() =>
        activeCompanies.value.find(company => company.id === defaultWireDoc.value?.company_id),
    );

    const getActiveCompanies = () => executeCompanies();

    // branches
    const {
        data: branches,
        isFetching: isFetchingBranches,
        execute: executeBranches,
    } = companyFetch(`/api/user/branches?active=true&page=${DEFAULT_PAGE}&per_page=${DEFAULT_PER_PAGE}`, {
        immediate: false,
    }).json<PaginatedResponse<Branch>>();

    const activeBranches = computed<Branch[]>(() => branches.value?.data ?? []);

    const defaultWireDocBranch = computed(() =>
        activeBranches.value.find(branch => branch.id === defaultWireDoc.value?.branch_id),
    );

    const getActiveBranches = () => executeBranches();

    // wire docs
    const {
        data: wireDocs,
        isFetching: isFetchingWireDocs,
        execute: executeWireDocs,
    } = companyFetch(`/api/user/wireDocs?page=${DEFAULT_PAGE}&per_page=${DEFAULT_PER_PAGE}`, { immediate: false }).json<
        PaginatedResponse<AvailableWireDoc>
    >();

    const availableWireDocs = computed<AvailableWireDoc[]>(() => wireDocs.value?.data ?? []);

    const getAvailableWireDocs = () => executeWireDocs();

    return {
        // file details
        showBalanceDue,
        showCloseDate,
        showNotes,

        // default wire doc
        defaultWireDoc,

        // disbursements
        isDisbursementApprover,
        isDisbursementApprovalsEnabled,

        // client type requirements
        idvDependentClientTypes,
        requireIdvClientTypes,
        requireMfaClientTypes,

        // companies
        activeCompanies,
        defaultWireDocCompany,
        getActiveCompanies,
        isFetchingCompanies,

        // branches
        activeBranches,
        defaultWireDocBranch,
        getActiveBranches,
        isFetchingBranches,

        // wire docs
        availableWireDocs,
        getAvailableWireDocs,
        isFetchingWireDocs,

        // me detail
        isFetchingMeDetail,
        isPriorityUser: computed(() => meDetail.value?.data.account.isPriority),
    };
});
